import React, { useState } from "react";
import { Lightbulb, Send, X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";

const CATEGORY_LABELS = {
  idea: "💡 Nápad na vylepšenie",
  bug: "🐞 Niečo nefunguje",
  praise: "❤️ Pochvala / poďakovanie",
  other: "✉️ Iné",
};

/** Floating bottom-right feedback button + modal. Hidden for non-logged users. */
export default function FeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!user || user.role === "admin") return null;       // Hide for admin & guests
  if (location.pathname.startsWith("/admin")) return null; // Don't show on admin pages

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post("/feedback", {
        category,
        message: message.trim(),
        page: location.pathname,
      });
      setDone(true);
      setTimeout(() => {
        setOpen(false); setDone(false); setMessage(""); setCategory("idea");
      }, 1800);
    } catch (e) {
      alert("Chyba pri odoslaní: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        data-testid="feedback-launcher"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 left-4 sm:left-6 z-30 w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 shadow-xl hover:shadow-yellow-500/50 hover:scale-105 transition-all flex items-center justify-center group"
        title="Pošli nápad / feedback"
      >
        <Lightbulb className="w-5 h-5 text-black group-hover:rotate-12 transition-transform" strokeWidth={2.5} />
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" data-testid="feedback-modal">
          <div className="bg-[var(--wb-surface)] border border-yellow-700/40 max-w-md w-full p-6 relative shadow-2xl">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-zinc-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>

            {done ? (
              <div className="py-8 text-center" data-testid="feedback-success">
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-3" />
                <h3 className="font-display font-bold text-xl text-white">Vďaka!</h3>
                <p className="text-sm text-zinc-400 mt-2">Tvoj odkaz prišiel adminovi. Pozrieme to.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-black" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display font-bold text-lg">Schránka nápadov</h3>
                </div>
                <p className="text-xs text-zinc-500 -mt-2">
                  Pošli adminovi nápad, návrh alebo problém. Tvoja správa nie je verejne viditeľná.
                </p>

                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Kategória</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="wb-input w-full px-3 py-2 text-sm"
                    data-testid="feedback-category"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
                    Tvoja správa ({message.length}/4000)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    maxLength={4000}
                    placeholder="Napíš čo by sa mohlo zlepšiť, čo nefunguje, alebo čo by si rád videl..."
                    className="wb-input w-full px-3 py-2 text-sm font-mono"
                    data-testid="feedback-message"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy || message.trim().length < 3}
                  className="wb-btn-primary w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="feedback-submit"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Odoslať
                </button>

                <p className="text-[10px] text-zinc-600 text-center font-mono">
                  Posiela sa adminovi · NIE je verejné · žiadne notifikácie ostatným
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
